<script>
	import * as Yup from "yup";
	import Forms, { useValidations } from "../ursCommon/Forms.svelte";
	import { FormGroup, Input } from "@sveltestrap/sveltestrap";
	import { baseUrl, isValidDate } from "../ursCommon/Common.svelte";
	import { Loader } from "../ursCommon/GlobalLoader.svelte";
	import { parse } from "svelte/compiler";
	import { dateStuff } from "../common/Date.svelte";

	const { validateField, validateForm, getErrors, resetErrors, removeError } =
		useValidations();
	const {
		toLocalMidnight,
		getISODateOnly,
		addDays,
		addMonths,
		addYears,
		parseDateOnly,
		isValidISODate,
		normalizeDate,
	} = dateStuff();

	let { disabled = true} = $props();

	let errors = $derived(getErrors());
	let formHasErrors = $derived(Object.keys(errors).length ? true : false);
	let prompt;
	let dateRange;
	let submissionDate = toLocalMidnight(new Date());

	let formData = $state({
		beginDate: getISODateOnly(submissionDate),
		endDate: (addYears(getISODateOnly(submissionDate), 1)),
	});

	const MAX_BEGIN_OFFSET_DAYS = 45;

	let beginDate = $state(getISODateOnly(submissionDate));
	let minBeginDate = $derived(getISODateOnly(submissionDate));
	let minEndDate = $derived.by(() => {
		if (isValidISODate(formData.beginDate)) {
			return formData.beginDate;
			} else {
				return getISODateOnly(submissionDate);
			}
	});
	let maxBeginDate = $derived.by(() => {
		var rtn = addDays(getISODateOnly(submissionDate), 45);
		return rtn instanceof Date ? getISODateOnly(rtn) : rtn;
	});
	let maxEndDate = $derived.by(() => {
		var rtn = isValidISODate(formData.beginDate)
			? addYears(formData.beginDate, 1)
			: addYears(getISODateOnly(submissionDate), 1);
		return rtn instanceof Date ? getISODateOnly(rtn) : rtn;
	});

	const baseSchema = Yup.object({
		beginDate: Yup.date()
			.typeError("Begin date must be a valid date")
			.required("Begin date is required")
    .test('begin-not-in-past', 'Begin date must be today or later', (b) => {
      if (!b) return true;
      const today = toLocalMidnight(new Date());
      return b >= today;
    })
    .test(
      'begin-within-45-days',
      `Begin date must be no later than ${MAX_BEGIN_OFFSET_DAYS} days from today`,
      (b) => {
        if (!b) return true;
        const max = addDays(toLocalMidnight(new Date()), MAX_BEGIN_OFFSET_DAYS);
        return b <= max;
      }
    ),
		endDate: Yup.date()
			.typeError("End date must be a valid date")
			.required("End date is required")
    .test(
      'end-date-rules',
      // One message or make this dynamic (see below)
      'End date must be on/after begin date and within one calendar year of begin date',
      function (end) {
        // Read sibling from parent and normalize both
        const rawBegin = this.parent?.beginDate;
        const b = normalizeDate(rawBegin);
        const e = normalizeDate(end);
        // If either is missing/invalid, skip; required/typeError will flag separately
        if (!b || !e) return true;

        // Rule 1: end >= begin
        if (e < b) {
          return this.createError({
            path: this.path,
            message: 'End date must be on or after the begin date',
          });
        }

        // Rule 2: end <= begin + 1 calendar year (inclusive)
        const max = addYears(b, 1);
        if (e > max) {
          return this.createError({
            path: this.path,
            message:
              'End date must be within one calendar year of the begin date',
          });
        }
        return true;
      }
    ),
	});

	function validateendDate() {
		const beginDate = formData.beginDate;
		if (!beginDate) return true;
		const limit = addMonths(new Date(beginDate), 12);
		return formData.endDate <= limit;
	}

	let formSchema = $derived.by(() => {
		let schema = baseSchema;
		return schema;
	});

	export function clearAllErrors() {
		resetErrors();
	}

	export function getValues() {
		return formData;
	}

	export function setValues(data) {
		formData.beginDate = data.beginDate;
		formData.endDate = data.endDate;
		resetErrors();
	}

	export function hasErrors() {
		return formHasErrors;
	}

	export async function validate() {
		await validateForm(formSchema, formData);
	}

	$effect(() => {
		formHasErrors = Object.keys(errors).length ? true : false;
	});

	function handleOtherMedicationChange() {
		validateField(formSchema, "otherMedication", formData);
	}

	function handleChange(fieldName, fieldValue) {
		const vars = new Map();
		vars.set(fieldName, fieldValue);
		const data = Object.fromEntries(vars);
		for (const key in data) {
			// console.log(`${key}: ${data[key]}`);
			validateField(formSchema, key, formData);
		}
	}

	function handleBeginDateChange(fieldName, e) {
		console.log("handleBeginDateChange", e);
		const val = e;
		console.log(e);
		handleChange(fieldName, e);
		if (isValidISODate(formData.beginDate)) {
			formData.endDate = addYears(formData.beginDate, 1);
		}
		validateField(formSchema, "endDate", formData);
	}
</script>

<main>
	<div class="row">
		<div class="col-xs-12 col-sm-12 col-md-3">
			<FormGroup floating label="Begin Date">
				<Input
					id="beginDate"
					name="beginDate"
					type="date"
					bind:value={formData.beginDate}
					min={minBeginDate}
					max={maxBeginDate}
					invalid={Boolean(errors.beginDate)}
					feedback={errors.beginDate}
					on:change={(e) => handleBeginDateChange("beginDate", e.target.value)}
					disabled={disabled}
				/>
			</FormGroup>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-3">
			<FormGroup floating label="End Date">
				<Input
					id="endDate"
					name="endDate"
					type="date"
					bind:value={formData.endDate}
					min={minEndDate}
					max={maxEndDate}
					invalid={Boolean(errors.endDate)}
					feedback={errors.endDate}
					on:change={(e) => handleChange("endDate", e.target.value)}
					disabled={disabled}
				/>
			</FormGroup>
		</div>
	</div>
</main>

<style>
	.radioButtonInvalid {
		color: var(--bs-form-invalid-color);
		font-size: 0.875em;
		margin-top: 0.25rem;
		width: 100%;
	}
</style>
